/**
 * Design Login Form Component
 * デザインHTMLに基づくログインフォーム
 * Requirements: 6.1, 6.2, 6.5
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { loginSchema, type LoginFormData } from '../schemas/auth.schema';
import { useAuth } from '../context/AuthContext';
import './DesignLoginForm.scss';

export const DesignLoginForm: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const usernameValue = watch('username');
  const passwordValue = watch('password');

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data.username, data.password);
      navigate('/dashboard');
    } catch {
      // Error is handled by AuthContext
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="design-login-page">
      <div className="l-all-wrap">
        <header id="l-header" className="l-header">
          <div className="inner">
            <p className="logo">
              <Link to="/" className="logo_link">
                <img src="/mock/terrace.png" alt="GB照らす" />
              </Link>
            </p>
          </div>
        </header>

        <main className="l-main">
          <section className="p-login">
            <div className="login">
              <form
                id="js-login"
                className="login_form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <h1 className="login_ttl">GBマイページ ログイン</h1>

                {error && (
                  <div className="login_error_alert">
                    {error}
                  </div>
                )}

                <ul className="login_list">
                  <li className="login_list_item">
                    <label className={`login_list_label ${!usernameValue ? 'placeholder-shown' : ''}`}>
                      <input
                        {...register('username')}
                        id="js-login-account"
                        className="login_list_label_input"
                        type="text"
                        placeholder="アカウント名"
                        autoComplete="username"
                        autoFocus
                        disabled={isLoading}
                      />
                    </label>
                    {errors.username && (
                      <p className="login_list_error">{errors.username.message}</p>
                    )}
                  </li>
                  <li className="login_list_item">
                    <div className="login_list_box">
                      <label className={`login_list_label ${!passwordValue ? 'placeholder-shown' : ''}`}>
                        <input
                          {...register('password')}
                          id="js-login-pass"
                          className="login_list_label_input"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="パスワード"
                          autoComplete="current-password"
                          disabled={isLoading}
                        />
                      </label>
                      <button
                        id="js-login-pass-toggle"
                        className={`login_list_label_toggle ${showPassword ? 'show-password' : 'hide-password'}`}
                        type="button"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                      />
                    </div>
                    <p className="login_list_note">半角英数字のみ・8文字以上</p>
                    {errors.password && (
                      <p className="login_list_error">{errors.password.message}</p>
                    )}
                  </li>
                </ul>

                <button
                  id="js-login-btn"
                  className="login_btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <span className="login_loading" /> : 'ログイン'}
                </button>
                <p className="login_forgot">
                  ID/パスワードを忘れた方は{' '}
                  <Link to="/forgot-password" className="login_forgot_link">
                    こちら
                  </Link>
                </p>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
